import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { RevealLines, Reveal } from "../Reveal";
import { ArrowUpRight } from "lucide-react";

const EXEC_IMG = "https://static.prod-images.emergentagent.com/jobs/01bf850e-1aa5-4eff-9307-bea3aa5e9f7d/images/6b749fc2e112f7b25bda872180af8abff2c6e2d81dc678b2f373bb291e517fac.jpeg";

export default function Hero() {
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

  return (
    <section ref={ref} className="relative min-h-screen bg-navy grain pt-[72px] overflow-hidden" data-testid="hero-section">
      {/* Full-bleed executive + AI visualization background with parallax */}
      <motion.img src={EXEC_IMG} alt="Executive reviewing an AI career dashboard"
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 w-full h-full object-cover object-center" />
      <div className="absolute inset-0 bg-navy/72" />
      <div className="absolute inset-0 bg-gradient-to-r from-navy/90 via-navy/50 to-transparent" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 min-h-[calc(100vh-72px)] flex flex-col justify-center py-20">
        <Reveal delay={0.1}>
          <div className="flex items-center gap-3 mb-8">
            <span className="overline text-cobalt-light">Joy Resume Builders & Consulting</span>
            <span className="h-px w-14 bg-white/40" />
          </div>
        </Reveal>

        <h1 className="font-display text-[13vw] leading-[0.92] sm:text-7xl lg:text-[5.6rem] font-bold tracking-tight text-white max-w-4xl">
          <RevealLines delay={0.25} lines={["Your career,", "written to"]} />
          <span className="line-mask">
            <motion.span className="block italic text-cobalt-light"
              initial={{ y: "110%" }} animate={{ y: "0%" }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.55 }}>
              command the room.
            </motion.span>
          </span>
        </h1>

        <Reveal delay={0.8}>
          <p className="mt-8 text-lg text-white/70 max-w-xl leading-relaxed">
            We craft resumes, LinkedIn profiles and career narratives for technical and executive professionals —
            then connect you with roles worth chasing.
          </p>
        </Reveal>

        <Reveal delay={0.95}>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button data-testid="hero-cta-review" onClick={() => navigate("/resume-review")}
              className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-cobalt text-white font-medium hover:bg-white hover:text-navy transition-colors">
              Get a free AI review
              <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button data-testid="hero-cta-jobs" onClick={() => navigate("/jobs")}
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-white/40 text-white font-medium hover:bg-white hover:text-navy transition-colors">
              Browse opportunities
            </button>
          </div>
        </Reveal>

        <Reveal delay={1.1} y={40}>
          <div className="mt-14 inline-flex items-center gap-6 bg-white/10 backdrop-blur-md border border-white/15 px-6 py-4">
            <div>
              <p className="font-mono-ui text-[0.62rem] text-cobalt-light uppercase tracking-widest">Since 2016</p>
              <p className="font-display text-2xl text-white mt-1">3,400+ careers elevated</p>
            </div>
            <span className="h-10 w-px bg-white/20" />
            <div>
              <p className="font-mono-ui text-[0.62rem] text-cobalt-light uppercase tracking-widest">Callback rate</p>
              <p className="font-display text-2xl text-white mt-1">92%</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
